import { ReactElement } from "react";

import dayjs from "dayjs";
import { Button, Heading } from "react-bulma-components";
import styled from "styled-components";

import { PriceLabel } from "components/PriceLabel/PriceLabel";
import { TransitsTable } from "components/TransitsTable/TransitsTable";
import { PreparedRoutesByWorkplace } from "containers/Routes/Routes";
import { showCommentInHTML } from "helpers/comment";
import { slashedYMD, cutOffDate } from "helpers/date";
import { getDistanceKmString } from "helpers/distance";
import { PreparedRoute } from "models/preparedRoute";
import * as Layout from "molecules/Layout";
import * as Table from "molecules/Table";

const ExpirationWrapper = styled.span`
  line-height: 1.8rem;
`;
const EditExpirationButtonWrapper = styled.span`
  margin-left: 1rem;
`;

const renderRoutes = (
  isUpdatePreparedRoutesInProgress: boolean,
  isApprover: boolean,
  preparedRoutesByWorkplace: PreparedRoutesByWorkplace,
  changePreparedRoute?: (
    arrivalId: number,
    arrivalName: string,
    departureId: number,
    departureName: string,
    preparedRoute: PreparedRoute
  ) => void,
  toggleRouteApplicationDialogToApply?: (preparedRoute: PreparedRoute) => void,
  toggleRouteApplicationDialogToCancel?: (preparedRoute: PreparedRoute) => void,
  updatePreparedRoutePriority?: (preparedRoute: PreparedRoute) => void,
  toggleExpirationDateDialog?: (preparedRoute: PreparedRoute) => void
) => {
  if (preparedRoutesByWorkplace.preparedRoutes.length === 0) {
    return <div>登録済みの通勤経路はありません</div>;
  }

  return (
    <>
      {preparedRoutesByWorkplace.preparedRoutes.map((preparedRoute) => (
        <Table.Wrapper key={preparedRoute.id}>
          <Table.RouteTable size="narrow">
            <tbody>
              <tr>
                <Table.RouteTh>ID</Table.RouteTh>
                <Table.Td>{preparedRoute.id}</Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>申請ステータス</Table.RouteTh>
                <Table.Td>{preparedRoute.approvalStatusName}</Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>出発地</Table.RouteTh>
                <Table.Td>
                  {preparedRoute.departureName}
                  <small className="has-text-grey">
                    {`（ID: ${preparedRoute.departureId}）`}
                  </small>
                  <small className="has-text-grey">
                    〜 {slashedYMD(preparedRoute.departureEndDate)}
                  </small>
                  <br />
                  <small className="has-text-grey">
                    {preparedRoute.departureAddress}
                  </small>
                </Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>到着地</Table.RouteTh>
                <Table.Td>
                  {preparedRoute.arrivalName}
                  <small className="has-text-grey">
                    {`（${preparedRoute.arrivalCode}）`}
                  </small>
                  <br />
                  <small className="has-text-grey">
                    住所: {preparedRoute.arrivalAddress}
                  </small>
                </Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>総移動距離</Table.RouteTh>
                <Table.Td>
                  {getDistanceKmString(preparedRoute.totalDistanceKm)}
                </Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>合計精算額</Table.RouteTh>
                <Table.Td>
                  <PriceLabel amount={preparedRoute.totalFare} />
                </Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>
                  {isApprover ? "申請者" : "あなた"}
                  のコメント
                </Table.RouteTh>
                <Table.Td>
                  {showCommentInHTML(preparedRoute.applicantComment)}
                </Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>
                  {isApprover ? "あなた" : "承認者"}
                  のコメント
                </Table.RouteTh>
                <Table.Td>
                  {showCommentInHTML(preparedRoute.approverComment)}
                </Table.Td>
              </tr>
              <tr>
                <Table.RouteTh>有効期限</Table.RouteTh>
                <Table.Td>
                  <ExpirationWrapper>
                    {`${slashedYMD(preparedRoute.startDate)} - ${slashedYMD(
                      preparedRoute.endDate
                    )}`}
                  </ExpirationWrapper>
                  {toggleExpirationDateDialog && (
                    <EditExpirationButtonWrapper>
                      <Button
                        size="small"
                        onClick={() =>
                          toggleExpirationDateDialog(preparedRoute)
                        }
                      >
                        <i className="fas fa-edit" />
                      </Button>
                    </EditExpirationButtonWrapper>
                  )}
                </Table.Td>
              </tr>
            </tbody>
          </Table.RouteTable>

          <Layout.RouteActionWrapper justifyContent="flex-end">
            {toggleRouteApplicationDialogToApply &&
              preparedRoute.approvalStatus === "unapplied" &&
              preparedRoute.endDate > new Date() && (
                <Button
                  disabled={isUpdatePreparedRoutesInProgress}
                  color="primary"
                  onClick={() =>
                    toggleRouteApplicationDialogToApply(preparedRoute)
                  }
                >
                  申請
                </Button>
              )}
            {toggleRouteApplicationDialogToCancel &&
              preparedRoute.approvalStatus === "applying" &&
              preparedRoute.endDate > new Date() && (
                <Button
                  disabled={isUpdatePreparedRoutesInProgress}
                  onClick={() =>
                    toggleRouteApplicationDialogToCancel(preparedRoute)
                  }
                >
                  取下げ
                </Button>
              )}
            {changePreparedRoute &&
              preparedRoute.approvalStatus === "unapplied" &&
              preparedRoute.endDate > new Date() && (
                <Button
                  disabled={isUpdatePreparedRoutesInProgress}
                  onClick={() =>
                    changePreparedRoute(
                      preparedRoutesByWorkplace.arrivalId,
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      preparedRoutesByWorkplace.arrivalName!,
                      preparedRoutesByWorkplace.departureId,
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      preparedRoutesByWorkplace.departureName!,
                      preparedRoute
                    )
                  }
                >
                  修正
                </Button>
              )}
            {updatePreparedRoutePriority && preparedRoute.endDate > new Date() && (
              <Button
                disabled={
                  isUpdatePreparedRoutesInProgress || preparedRoute.priority
                }
                onClick={() => updatePreparedRoutePriority(preparedRoute)}
              >
                既定
              </Button>
            )}
          </Layout.RouteActionWrapper>
          <TransitsTable
            preparedRouteDetails={preparedRoute.preparedRouteDetails}
          />
        </Table.Wrapper>
      ))}
    </>
  );
};

export interface RoutesTableProps {
  preparedRoutesByWorkplaces: PreparedRoutesByWorkplace[];
  isPreparedRoutesLoaded: boolean;
  isUpdatePreparedRoutesInProgress: boolean;
  isApprover?: boolean;
  changePreparedRoute?: (
    arrivalId: number,
    arrivalName: string,
    departureId: number,
    departureName: string,
    preparedRoute: PreparedRoute
  ) => void;
  toggleNewRouteDialog?: (
    arrivalId?: number,
    arrivalName?: string,
    departureId?: number,
    departureName?: string
  ) => void;
  toggleRouteApplicationDialogToApply?: (preparedRoute: PreparedRoute) => void;
  toggleRouteApplicationDialogToCancel?: (preparedRoute: PreparedRoute) => void;
  updatePreparedRoutePriority?: (preparedRoute: PreparedRoute) => void;
  toggleExpirationDateDialog?: (preparedRoute: PreparedRoute) => void;
}

const isInvalidatedAddress = (date: Date | undefined): boolean => {
  const now = dayjs(new Date());
  const cutOff = cutOffDate(now);

  if (now.get("month") === cutOff.get("month")) {
    if (now < cutOff) {
      return cutOff.subtract(1, "month").set("day", 1) > dayjs(date);
    }
    return cutOff.set("day", 1) > dayjs(date);
  }
  return cutOff.set("day", 1) > dayjs(date);
};

export function RoutesTable({
  isPreparedRoutesLoaded,
  isUpdatePreparedRoutesInProgress,
  isApprover,
  preparedRoutesByWorkplaces,
  changePreparedRoute,
  toggleNewRouteDialog,
  toggleRouteApplicationDialogToApply,
  toggleRouteApplicationDialogToCancel,
  updatePreparedRoutePriority,
  toggleExpirationDateDialog,
}: RoutesTableProps): ReactElement | null {
  if (!isPreparedRoutesLoaded) {
    return <Layout.PreparedRoutesByWorkplacesWrapper />;
  }

  if (preparedRoutesByWorkplaces.length > 0) {
    return (
      <>
        {preparedRoutesByWorkplaces.map((preparedRoutesByWorkplace) => (
          <Layout.PreparedRoutesByWorkplacesWrapper
            key={preparedRoutesByWorkplace.arrivalId}
          >
            <Layout.FlexWrapper>
              <Heading size={4}>
                {preparedRoutesByWorkplace.arrivalName} への通勤経路
                <Heading size={6} subtitle weight="light" renderAs="div">
                  {`（住所ID: ${preparedRoutesByWorkplace.arrivalId}）`}
                </Heading>
              </Heading>
              {toggleNewRouteDialog && (
                <Button
                  className="button"
                  onClick={() =>
                    toggleNewRouteDialog(
                      preparedRoutesByWorkplace.arrivalId,
                      preparedRoutesByWorkplace.arrivalName,
                      preparedRoutesByWorkplace.departureId,
                      preparedRoutesByWorkplace.departureName
                    )
                  }
                  disabled={isInvalidatedAddress(
                    preparedRoutesByWorkplace.arrivalAddressInvalidatedAt
                  )}
                >
                  新規経路
                </Button>
              )}
            </Layout.FlexWrapper>
            {renderRoutes(
              isUpdatePreparedRoutesInProgress,
              !!isApprover,
              preparedRoutesByWorkplace,
              changePreparedRoute,
              toggleRouteApplicationDialogToApply,
              toggleRouteApplicationDialogToCancel,
              updatePreparedRoutePriority,
              toggleExpirationDateDialog
            )}
          </Layout.PreparedRoutesByWorkplacesWrapper>
        ))}
      </>
    );
  }
  return (
    <Layout.PreparedRoutesByWorkplacesWrapper>
      登録済みの通勤経路はありません
    </Layout.PreparedRoutesByWorkplacesWrapper>
  );
}
